export type AiBuilderQuestionSetQuestion = {
  questionId: string;
  selectionMode: 'single' | 'multiple';
  allowDirectInput: boolean;
};

export type AiBuilderQuestionSetResponse = {
  choiceOptionIds: readonly string[];
  text: string;
  directInputSelected: boolean;
};

export type AiBuilderQuestionSetState<
  Question extends AiBuilderQuestionSetQuestion,
> = {
  interactionId: string;
  activeQuestionId: string;
  questions: readonly Question[];
  responsesByQuestionId: Partial<Record<string, AiBuilderQuestionSetResponse>>;
  cancelled: boolean;
  submissionError: string | null;
};

export type AiBuilderQuestionSetAction =
  | { type: 'navigatePrevious' }
  | { type: 'navigateNext' }
  | { type: 'navigateNextUnanswered'; questionId: string }
  | { type: 'selectSingleChoice'; questionId: string; optionId: string }
  | {
      type: 'updateMultipleChoices';
      questionId: string;
      optionIds: readonly string[];
    }
  | {
      type: 'setDirectInputSelected';
      questionId: string;
      selected: boolean;
    }
  | { type: 'updateDirectText'; questionId: string; text: string }
  | { type: 'cancel' }
  | { type: 'setSubmissionError'; error: string }
  | { type: 'clearSubmissionError' };

const EMPTY_RESPONSE: AiBuilderQuestionSetResponse = {
  choiceOptionIds: [],
  text: '',
  directInputSelected: false,
};

export const createAiBuilderQuestionSetState = <
  Question extends AiBuilderQuestionSetQuestion,
>({
  interactionId,
  questions,
  activeQuestionId = questions[0]?.questionId,
  responsesByQuestionId = {},
  cancelled = false,
  submissionError = null,
}: {
  interactionId: string;
  questions: readonly Question[];
  activeQuestionId?: string;
  responsesByQuestionId?: Partial<Record<string, AiBuilderQuestionSetResponse>>;
  cancelled?: boolean;
  submissionError?: string | null;
}): AiBuilderQuestionSetState<Question> => {
  const validActiveQuestionId = assertQuestionSetInvariants({
    activeQuestionId,
    questions,
  });

  return {
    interactionId,
    activeQuestionId: validActiveQuestionId,
    questions,
    responsesByQuestionId: normalizeResponses(questions, responsesByQuestionId),
    cancelled,
    submissionError,
  };
};

export const reduceAiBuilderQuestionSetState = <
  Question extends AiBuilderQuestionSetQuestion,
>(
  state: AiBuilderQuestionSetState<Question>,
  action: AiBuilderQuestionSetAction,
): AiBuilderQuestionSetState<Question> => {
  if (state.cancelled) return state;

  switch (action.type) {
    case 'navigatePrevious':
      return navigateByOffset(state, -1);
    case 'navigateNext':
      return navigateByOffset(state, 1);
    case 'navigateNextUnanswered':
      return navigateToNextUnanswered(state, action.questionId);
    case 'selectSingleChoice':
      return selectSingleChoice(state, action.questionId, action.optionId);
    case 'updateMultipleChoices':
      return updateMultipleChoices(state, action.questionId, action.optionIds);
    case 'setDirectInputSelected':
      return setDirectInputSelected(state, action.questionId, action.selected);
    case 'updateDirectText':
      return updateDirectText(state, action.questionId, action.text);
    case 'cancel':
      return {
        ...state,
        responsesByQuestionId: {},
        cancelled: true,
        submissionError: null,
      };
    case 'setSubmissionError':
      return { ...state, submissionError: action.error };
    case 'clearSubmissionError':
      return { ...state, submissionError: null };
  }
};

export const getAnsweredQuestionIds = <
  Question extends AiBuilderQuestionSetQuestion,
>(
  state: AiBuilderQuestionSetState<Question>,
): readonly string[] =>
  state.questions
    .filter(question => getActiveQuestionAnswer(state, question.questionId))
    .map(question => question.questionId);

export const canSubmitAiBuilderQuestionSet = <
  Question extends AiBuilderQuestionSetQuestion,
>(
  state: AiBuilderQuestionSetState<Question>,
): boolean =>
  !state.cancelled &&
  state.questions.every(question =>
    Boolean(getActiveQuestionAnswer(state, question.questionId)),
  );

export const getNextUnansweredQuestionId = <
  Question extends AiBuilderQuestionSetQuestion,
>(
  state: AiBuilderQuestionSetState<Question>,
  fromQuestionId: string,
): string | null => {
  const fromIndex = findQuestionIndex(state.questions, fromQuestionId);
  if (fromIndex === -1) return null;

  for (let offset = 1; offset < state.questions.length; offset += 1) {
    const question =
      state.questions[(fromIndex + offset) % state.questions.length];
    if (question && !getActiveQuestionAnswer(state, question.questionId)) {
      return question.questionId;
    }
  }

  return null;
};

export const getActiveQuestionAnswer = <
  Question extends AiBuilderQuestionSetQuestion,
>(
  state: AiBuilderQuestionSetState<Question>,
  questionId: string,
) => {
  const question = findQuestion(state.questions, questionId);
  const response = state.responsesByQuestionId[questionId];
  if (!question || !response) return null;

  const optionIds = normalizeOptionIds(response.choiceOptionIds);
  const hasDirectText =
    question.allowDirectInput &&
    response.directInputSelected &&
    Boolean(response.text.trim());

  if (question.selectionMode === 'single') {
    if (response.directInputSelected) {
      return hasDirectText
        ? ({ mode: 'text', text: response.text } as const)
        : null;
    }
    return optionIds.length === 1
      ? ({ mode: 'choice', optionIds } as const)
      : null;
  }

  if (response.directInputSelected && !hasDirectText) return null;
  if (optionIds.length === 0 && !hasDirectText) return null;
  if (optionIds.length > 0 && hasDirectText) {
    return { mode: 'mixed', optionIds, text: response.text } as const;
  }
  return hasDirectText
    ? ({ mode: 'text', text: response.text } as const)
    : ({ mode: 'choice', optionIds } as const);
};

const navigateByOffset = <Question extends AiBuilderQuestionSetQuestion>(
  state: AiBuilderQuestionSetState<Question>,
  offset: -1 | 1,
): AiBuilderQuestionSetState<Question> => {
  const activeIndex = findQuestionIndex(
    state.questions,
    state.activeQuestionId,
  );
  const nextQuestion = state.questions[activeIndex + offset];
  return nextQuestion
    ? { ...state, activeQuestionId: nextQuestion.questionId }
    : state;
};

const navigateToNextUnanswered = <
  Question extends AiBuilderQuestionSetQuestion,
>(
  state: AiBuilderQuestionSetState<Question>,
  questionId: string,
): AiBuilderQuestionSetState<Question> => {
  const question = findQuestion(state.questions, questionId);
  if (!question || !getActiveQuestionAnswer(state, questionId)) return state;

  const nextQuestionId = getNextUnansweredQuestionId(state, questionId);
  return nextQuestionId
    ? { ...state, activeQuestionId: nextQuestionId }
    : state;
};

const selectSingleChoice = <Question extends AiBuilderQuestionSetQuestion>(
  state: AiBuilderQuestionSetState<Question>,
  questionId: string,
  optionId: string,
): AiBuilderQuestionSetState<Question> => {
  const question = findQuestion(state.questions, questionId);
  if (!question || question.selectionMode !== 'single' || !optionId.trim()) {
    return state;
  }
  return updateResponse(state, questionId, response => ({
    ...response,
    choiceOptionIds: [optionId],
    directInputSelected: false,
  }));
};

const updateMultipleChoices = <Question extends AiBuilderQuestionSetQuestion>(
  state: AiBuilderQuestionSetState<Question>,
  questionId: string,
  optionIds: readonly string[],
): AiBuilderQuestionSetState<Question> => {
  const question = findQuestion(state.questions, questionId);
  if (!question || question.selectionMode !== 'multiple') return state;
  return updateResponse(state, questionId, response => ({
    ...response,
    choiceOptionIds: normalizeOptionIds(optionIds),
  }));
};

const setDirectInputSelected = <Question extends AiBuilderQuestionSetQuestion>(
  state: AiBuilderQuestionSetState<Question>,
  questionId: string,
  selected: boolean,
): AiBuilderQuestionSetState<Question> => {
  const question = findQuestion(state.questions, questionId);
  if (!question?.allowDirectInput) return state;
  return updateResponse(state, questionId, response => ({
    ...response,
    directInputSelected: selected,
  }));
};

const updateDirectText = <Question extends AiBuilderQuestionSetQuestion>(
  state: AiBuilderQuestionSetState<Question>,
  questionId: string,
  text: string,
): AiBuilderQuestionSetState<Question> => {
  const question = findQuestion(state.questions, questionId);
  if (!question?.allowDirectInput) return state;
  return updateResponse(state, questionId, response => ({
    ...response,
    text,
    directInputSelected: true,
  }));
};

const updateResponse = <Question extends AiBuilderQuestionSetQuestion>(
  state: AiBuilderQuestionSetState<Question>,
  questionId: string,
  updater: (
    response: AiBuilderQuestionSetResponse,
  ) => AiBuilderQuestionSetResponse,
): AiBuilderQuestionSetState<Question> => ({
  ...state,
  responsesByQuestionId: {
    ...state.responsesByQuestionId,
    [questionId]: updater(
      state.responsesByQuestionId[questionId] ?? EMPTY_RESPONSE,
    ),
  },
  submissionError: null,
});

const normalizeResponses = <Question extends AiBuilderQuestionSetQuestion>(
  questions: readonly Question[],
  responses: Partial<Record<string, AiBuilderQuestionSetResponse>>,
) =>
  Object.fromEntries(
    questions.flatMap(question => {
      const response = responses[question.questionId];
      if (!response || typeof response !== 'object') return [];
      if (!Array.isArray(response.choiceOptionIds)) return [];
      if (typeof response.text !== 'string') return [];
      if (typeof response.directInputSelected !== 'boolean') return [];
      return [
        [
          question.questionId,
          {
            choiceOptionIds: normalizeOptionIds(response.choiceOptionIds),
            text: response.text,
            directInputSelected: response.directInputSelected,
          },
        ] as const,
      ];
    }),
  );

const normalizeOptionIds = (optionIds: readonly string[]) => [
  ...new Set(optionIds.map(optionId => optionId.trim()).filter(Boolean)),
];

const findQuestionIndex = <Question extends AiBuilderQuestionSetQuestion>(
  questions: readonly Question[],
  questionId: string,
) => questions.findIndex(question => question.questionId === questionId);

const findQuestion = <Question extends AiBuilderQuestionSetQuestion>(
  questions: readonly Question[],
  questionId: string,
) => questions.find(question => question.questionId === questionId);

const assertQuestionSetInvariants = <
  Question extends AiBuilderQuestionSetQuestion,
>({
  activeQuestionId,
  questions,
}: {
  activeQuestionId: string | undefined;
  questions: readonly Question[];
}): string => {
  if (questions.length === 0) {
    throw new Error(
      'AI builder question set must contain at least one question.',
    );
  }

  const questionIds = questions.map(question => question.questionId);
  if (new Set(questionIds).size !== questionIds.length) {
    throw new Error('AI builder question ids must be unique.');
  }

  if (!activeQuestionId || !questionIds.includes(activeQuestionId)) {
    throw new Error(
      'AI builder active question must belong to the question set.',
    );
  }

  return activeQuestionId;
};
